import { useContext, useState } from 'react'
import Select from 'react-select'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import CombinedOutputData from '../graphData/CombinedOutputData'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'

/**
 * @typedef {object} OutputOption
 * @property {string} value - Output ID
 * @property {string} label - Output title
 */

/**
 * Output-comparison section on an activity deep-analysis page.
 *
 * The page's anchor activity is fixed; the user picks one or more of that
 * activity's own outputs and their results are charted together over time
 * (reuses the existing CombinedOutputData / StackedBarGraph). Display-only —
 * nothing is saved (ports the old CompareOutputs builder without its
 * save-to-Analysis behavior).
 *
 * Follows the page period (DeepAnalysisPeriodContext); when two periods are
 * compared the chart renders once per period, side by side.
 * AI-CONTEXT: See docs/features/deep-analysis-period.md
 *
 * @param {object} props
 * @param {object} props.activity - The anchor Activity doc
 */
const OutputCompareSection = ({ activity }) => {
    const [selectedOutputs, setSelectedOutputs] = useState(/** @type {string[]} */ ([]))
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const allOutputs = useFirestoreOrderBy('Outputs', 'Position', 'asc')
    const activityOutputs = (Array.isArray(allOutputs) ? allOutputs : [])
        .filter((o) => o.ActivityID === activity.ID && !o.Deleted)

    /** @type {OutputOption[]} */
    const outputOptions = activityOutputs.map((o) => ({ value: o.ID, label: o.Title }))

    const handleChange = (selected) => {
        setSelectedOutputs(selected ? selected.map((option) => option.value) : [])
    }

    /** @param {{start: string|null, end: string|null}|null} range - Period to chart (null = all data) */
    const renderChart = (range) => {
        const { startDate, endDate } = periodRangeToDates(range)
        return <CombinedOutputData outputIds={selectedOutputs} graphType='bar' startDate={startDate} endDate={endDate} />
    }

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Vergelijken'
                title='Outputs vergelijken'
                description={`Vergelijk de resultaten van één of meer outputs van "${activity.Activity}" naast elkaar.`}
            />
            {activityOutputs.length === 0 ? (
                <p className='deep-analysis-empty'>
                    Deze activiteit heeft nog geen outputs om te vergelijken.
                </p>
            ) : (
                <>
                    <Select
                        isMulti
                        options={outputOptions}
                        onChange={handleChange}
                        value={outputOptions.filter((option) => selectedOutputs.includes(option.value))}
                        placeholder='Selecteer outputs…'
                    />
                    {selectedOutputs.length > 0 && (compare
                        ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={renderChart} />
                        : renderChart(period?.rangeA || null))}
                </>
            )}
        </div>
    )
}

export default OutputCompareSection
